import { useState, useEffect, useCallback } from 'react';
import { Form, Row, Col, Alert } from 'react-bootstrap';
import { FaInfoCircle } from 'react-icons/fa';
import useSelectedDomain from '../../service/Hooks/useSelectedDomain';
import { getAccount } from '../../service/api/Order';
import SearchableSelect from '../../SearchableSelect';

const TransactionDetailsForm = ({
  formData,
  errors,
  selectedType,
  onFieldChange
}) => {
  const [accountCategories, setAccountCategories] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [loadingAccounts, setLoadingAccounts] = useState(false);
  const selectedDomain = useSelectedDomain();

  // Charger les catégories seulement une fois
  useEffect(() => {
    const loadCategories = async () => {
      const categories = [
        { code: 'NOMP', label: 'Nominatif Pur', description: 'Compte nominatif standard' },
        { code: 'CEPO', label: 'Émission Porteur', description: 'Émission au porteur' },
        { code: 'CENO', label: 'Émission Nominatif', description: 'Émission nominative' },
        { code: 'CGEM', label: 'Global d\'Émission', description: 'Compte global d\'émission' },
        { code: 'PORT', label: 'au Porteur', description: 'Compte au porteur' },
        { code: 'NOCH', label: 'Nominatif Changé', description: 'Nominatif changé' },
        { code: 'NPRO', label: 'Nominatif Propre', description: 'Nominatif propre' },
        { code: 'NOAD', label: 'Nominatif Administrateur', description: 'Nominatif administrateur' },
        { code: 'NADC', label: 'Nominatif Administrateur Changé', description: 'Nominatif administrateur changé' },
        { code: 'TIAN', label: 'Titre à Annuler', description: 'Titres en attente d\'annulation' },
        { code: 'TRNE', label: 'Transit', description: 'Compte de transit' },
        { code: 'TIRE', label: 'Titre Rendu', description: 'Titres rendus' },
        { code: 'GETR', label: 'Gestion de Titres', description: 'Compte de gestion' },
        { code: 'TNAF', label: 'Titre Non Affecté', description: 'Titres non affectés' },
        { code: 'TARE', label: 'Titre Affecté', description: 'Titres affectés' },
        { code: 'TALI', label: 'Titre Aliéné', description: 'Titres aliénés' },
        { code: 'REGU', label: 'Régularisation', description: 'Compte de régularisation' }
      ];
      setAccountCategories(categories);
    };
    
    loadCategories();
  }, []);

  // Charger les comptes quand le domaine est sélectionné
  useEffect(() => {
    const loadAccounts = async () => {
      if (!selectedDomain?.id) return;
      
      setLoadingAccounts(true);
      try {
        const accountsData = await getAccount(selectedDomain.id);
        console.log("Comptes trouvés:", accountsData);
        
        // Formater les comptes avec les informations directement de getAccount
        const formattedAccounts = accountsData.map(account => ({
          ...account,
          label: account.companyName || 
                 `${account.firstName || ''} ${account.lastName || ''}`.trim() || 
                 `Compte #${account.id}`,
          value: account.id,
          // IsManagedAccount est directement disponible dans account
          isManagedAccount: account.isManagedAccount === true || account.isManagedAccount === 1
        }));
        
        setAccounts(formattedAccounts);
        
      } catch (error) {
        console.error('Erreur chargement comptes:', error);
      } finally {
        setLoadingAccounts(false);
      }
    };

    loadAccounts();
  }, [selectedDomain]);

  // Fonction pour déterminer la catégorie en fonction du type de compte
  const determineAccountCategory = useCallback((isManagedAccount) => {
    if (isManagedAccount) {
      // Compte administré
      return 'NOAD'; // Nominatif Administrateur
    } else {
      // Compte non administré
      return 'NOMP'; // Nominatif Pur par défaut
    }
  }, []);

  // Gérer la sélection du compte source
  const handleFromAccountChange = useCallback((selectedAccount) => {
    const accountId = selectedAccount?.id || null;
    
    // Mettre à jour l'ID du compte source
    onFieldChange('fromAccountId', accountId);
    
    if (selectedAccount) {
      // Déterminer automatiquement la catégorie du compte source
      const isManaged = selectedAccount.isManagedAccount;
      const autoCategory = determineAccountCategory(isManaged);
      
      // Mettre à jour la catégorie du compte source
      onFieldChange('fromAccountCategory', autoCategory);
      
      // Pour une négociation, mettre aussi à jour la catégorie principale si vide
      if (selectedType?.value === 'Negociation' && !formData.accountCategory) {
        onFieldChange('accountCategory', autoCategory);
      }
    } else {
      // Réinitialiser si aucun compte sélectionné
      onFieldChange('fromAccountCategory', '');
    }
  }, [determineAccountCategory, onFieldChange, selectedType, formData.accountCategory]);

  // Gérer la sélection du compte destination
  const handleToAccountChange = useCallback((selectedAccount) => {
    const accountId = selectedAccount?.id || null;
    
    // Mettre à jour l'ID du compte destination
    onFieldChange('toAccountId', accountId);
    
    if (selectedAccount && selectedType?.value === 'Negociation' && !formData.accountCategory) {
      // Pour une négociation, mettre à jour la catégorie principale si c'est le compte destination
      const isManaged = selectedAccount.isManagedAccount;
      const autoCategory = determineAccountCategory(isManaged);
      onFieldChange('accountCategory', autoCategory);
    }
  }, [determineAccountCategory, onFieldChange, selectedType, formData.accountCategory]);

  // Formatter les comptes avec un badge indiquant s'ils sont administrés
  const formatAccountLabel = (account) => {
    const baseLabel = account.companyName || 
                     `${account.firstName || ''} ${account.lastName || ''}`.trim() || 
                     `Compte #${account.id}`;
    
    if (account.isManagedAccount) {
      return `${baseLabel}`; // Emoji pour indiquer un compte administré
    }
    
    return baseLabel;
  };

  // Récupérer l'objet compte sélectionné pour le compte source
  const getSelectedFromAccount = () => {
    if (!formData.fromAccountId) return null;
    return accounts.find(acc => acc.id === formData.fromAccountId);
  };

  // Récupérer l'objet compte sélectionné pour le compte destination
  const getSelectedToAccount = () => {
    if (!formData.toAccountId) return null;
    return accounts.find(acc => acc.id === formData.toAccountId);
  };

  if (!selectedType) return null;

  const formattedCategories = accountCategories.map(cat => ({
    ...cat,
    label: `${cat.label} (${cat.code})`,
    value: cat.code
  }));

  // Filtrer les catégories selon le type de transaction
  const filteredCategories = selectedType.categories 
    ? formattedCategories.filter(cat => selectedType.categories.includes(cat.value))
    : formattedCategories;

  const selectedFromAccount = getSelectedFromAccount();
  const selectedToAccount = getSelectedToAccount();

  return (
    <div className="step-content">
      {/* Quantité et Catégorie */}
      <Row className="g-3 mb-4">
        <Col md={6}>
          <Form.Group>
            <Form.Label className="fw-semibold">
              Quantité <span className="text-danger">*</span>
              <small className="text-muted ms-2">(Nombre de titres)</small>
            </Form.Label>
            <Form.Control
              type="number"
              min="1"
              step="1"
              value={formData.quantity}
              onChange={(e) => onFieldChange('quantity', parseInt(e.target.value) || '')}
              isInvalid={!!errors.quantity}
              placeholder="Ex: 1000"
            />
            <Form.Control.Feedback type="invalid">
              {errors.quantity}
            </Form.Control.Feedback>
            <Form.Text className="text-muted">
              Doit être un nombre entier positif
            </Form.Text>
          </Form.Group>
        </Col>

        <Col md={6}>
          <SearchableSelect
            label={
              <>
                Catégorie de compte <span className="text-danger">*</span>
                <small className="text-muted ms-2">(Type de portage)</small>
              </>
            }
            labelKey="label"
            valueKey="value"
            options={filteredCategories}
            value={formData.accountCategory}
            onChange={(selected) => onFieldChange('accountCategory', selected?.value || '')}
            placeholder="Rechercher une catégorie..."
            isClearable
            searchKeys={['label', 'code', 'description']}
            error={errors.accountCategory}
            required
            helpText={filteredCategories.find(c => c.value === formData.accountCategory)?.description}
          />
          {selectedType.categories && (
            <Form.Text className="text-muted">
              Catégories autorisées : {selectedType.categories.join(', ')}
            </Form.Text>
          )}
        </Col>
      </Row>

      {/* Nature de l'opération */}
      <div className="mb-4">
        <Form.Group>
          <Form.Label>
            Nature de l'opération
            <small className="text-muted ms-2">(Description facultative)</small>
          </Form.Label>
          <Form.Control
            as="textarea"
            rows={2}
            value={formData.operationNature || ''}
            onChange={(e) => onFieldChange('operationNature', e.target.value)}
            placeholder="Ex: Émission pour augmentation de capital, Conversion porteur en nominatif..."
          />
          <Form.Text className="text-muted">
            Décrivez brièvement l'opération pour la traçabilité
          </Form.Text>
        </Form.Group>
      </div>

      {/* Section Dates - OPTIONNELLE */}
      <div className="card mb-4">
        <div className="card-header bg-light">
          <h6 className="mb-0">Dates de l'opération (Optionnelles)</h6>
        </div>
        <div className="card-body">
          <Row className="g-3">
            <Col md={6}>
              <Form.Group>
                <Form.Label>
                  Date de l'opération
                  <small className="text-muted ms-2">(Date de réalisation)</small>
                </Form.Label>
                <Form.Control
                  type="date"
                  value={formData.dateOperation || ''}
                  onChange={(e) => onFieldChange('dateOperation', e.target.value || null)}
                  isInvalid={!!errors.dateOperation}
                />
                <Form.Control.Feedback type="invalid">
                  {errors.dateOperation}
                </Form.Control.Feedback>
                <Form.Text className="text-muted">
                  Laisser vide pour utiliser la date actuelle
                </Form.Text>
              </Form.Group>
            </Col>
            
            <Col md={6}>
              <Form.Group>
                <Form.Label>
                  Date de Compta.
                  <small className="text-muted ms-2">(Date de prise d'effet)</small>
                </Form.Label>
                <Form.Control
                  type="date"
                  value={formData.dateCredit || ''}
                  onChange={(e) => onFieldChange('dateCredit', e.target.value || null)}
                  isInvalid={!!errors.dateCredit}
                />
                <Form.Control.Feedback type="invalid">
                  {errors.dateCredit}
                </Form.Control.Feedback>
                <Form.Text className="text-muted">
                  Laisser vide pour utiliser la date actuelle
                </Form.Text>
              </Form.Group>
            </Col>
          </Row>
        </div>
      </div>

      {/* Compte source */}
      {selectedType.requires.fromAccount && (
        <div className="mb-4">
          <Alert variant="info" className="mb-3">
            <FaInfoCircle className="me-2" />
            <div>
              <strong>Compte source :</strong> Le compte d'où proviennent les titres
              {selectedType.value === 'Negociation' && (
                <small className="d-block text-muted mt-1">
                  💡 Pour une négociation, la catégorie sera automatiquement détectée 
                  selon le type de compte (administré ou non)
                </small>
              )}
            </div>
          </Alert>
          
          <Row className="g-3">
            <Col md={6}>
              <SearchableSelect
                label="Compte source"
                labelKey="label"
                valueKey="id"
                options={accounts.map(acc => ({
                  ...acc,
                  label: formatAccountLabel(acc)
                }))}
                value={formData.fromAccountId}
                onChange={handleFromAccountChange}
                placeholder={loadingAccounts ? "Chargement des comptes..." : "Rechercher un compte source..."}
                isClearable
                searchKeys={['companyName', 'firstName', 'lastName', 'accountNumber']}
                error={errors.fromAccountId}
                required
                isLoading={loadingAccounts}
                helpText={
                  selectedFromAccount
                    ? selectedFromAccount.isManagedAccount
                      ? "Compte administré  - Catégorie auto: NOAD"
                      : "Compte non administré - Catégorie auto: NOMP"
                    : ""
                }
              />
            </Col>
            <Col md={6}>
              <Form.Group>
                <Form.Label>
                  Catégorie du compte source
                  <small className="text-muted ms-2">(Auto-détectée)</small>
                </Form.Label>
                <Form.Control
                  type="text"
                  value={formData.fromAccountCategory || ''}
                  onChange={(e) => onFieldChange('fromAccountCategory', e.target.value)}
                  placeholder="Catégorie auto-détectée..."
                  readOnly
                  className="bg-light"
                />
                <Form.Text className="text-muted">
                  {formData.fromAccountCategory 
                    ? formData.fromAccountCategory === 'NOAD' 
                      ? 'Catégorie pour compte administré'
                      : 'Catégorie pour compte non administré'
                    : 'Sélectionnez un compte source pour détecter la catégorie'
                  }
                </Form.Text>
              </Form.Group>
            </Col>
          </Row>
        </div>
      )}

      {/* Compte destination */}
      {selectedType.requires.toAccount && (
        <div className="mb-4">
          <Alert variant="info" className="mb-3">
            <FaInfoCircle className="me-2" />
            <div>
              <strong>Compte destination :</strong> Le compte qui recevra les titres
              {selectedType.value === 'Negociation' && (
                <small className="d-block text-muted mt-1">
                  💡 Pour une négociation, la catégorie principale sera ajustée automatiquement
                </small>
              )}
            </div>
          </Alert>
          
          <SearchableSelect
            label="Compte destination"
            labelKey="label"
            valueKey="id"
            options={accounts.map(acc => ({
              ...acc,
              label: formatAccountLabel(acc)
            }))}
            value={formData.toAccountId}
            onChange={handleToAccountChange}
            placeholder={loadingAccounts ? "Chargement des comptes..." : "Rechercher un compte destination..."}
            isClearable
            searchKeys={['companyName', 'firstName', 'lastName', 'accountNumber']}
            error={errors.toAccountId}
            required
            isLoading={loadingAccounts}
            helpText={
              selectedToAccount
                ? selectedToAccount.isManagedAccount
                  ? "Compte administré "
                  : "Compte non administré"
                : ""
            }
          />
        </div>
      )}

      {/* Titre de conversion */}
      {selectedType.requires.conversion && (
        <div className="mb-4">
          <Alert variant="warning" className="mb-3">
            <FaInfoCircle className="me-2" />
            <strong>Titre de conversion :</strong> Sélectionnez le titre vers lequel convertir
          </Alert>
          
          <Form.Group>
            <Form.Label className="fw-semibold">
              ID du titre de conversion <span className="text-danger">*</span>
            </Form.Label>
            <Form.Control
              type="number"
              value={formData.conversionSecurityId || ''}
              onChange={(e) => onFieldChange('conversionSecurityId', e.target.value ? parseInt(e.target.value) : null)}
              isInvalid={!!errors.conversionSecurityId}
              placeholder="ID du titre de conversion"
            />
            <Form.Control.Feedback type="invalid">
              {errors.conversionSecurityId}
            </Form.Control.Feedback>
            <Form.Text className="text-muted">
              Entrez l'ID du titre de destination pour la conversion
            </Form.Text>
          </Form.Group>
        </div>
      )}

      {/* Résumé des règles */}
      <Alert variant="light" className="border">
        <h6 className="mb-2">Règles pour {selectedType.label} :</h6>
        <ul className="mb-0">
          <li>Quantité minimale : 1 titre</li>
          {selectedType.requires.fromAccount && <li>Un compte source est requis</li>}
          {selectedType.requires.toAccount && <li>Un compte destination est requis</li>}
          {selectedType.requires.conversion && <li>Un titre de conversion est requis</li>}
          <li>Les dates sont optionnelles</li>
          <li>Les documents sont optionnels (sauf pour certains types)</li>
          <li>La catégorie de compte doit être valide pour ce type de transaction</li>
          {selectedType.value === 'Negociation' && (
            <>
              <li>La catégorie est auto-détectée selon IsManagedAccount</li>
              <li>Catégorie auto: NOAD pour administré, NOMP pour non administré</li>
            </>
          )}
        </ul>
      </Alert>
    </div>
  );
};

export default TransactionDetailsForm;